import { createReducer } from 'redux-starter-kit';
import { selectRequest } from '../../../domain/requests/RequestReducer';
import {
  editChecklistCategoryNew, cancelEditChecklistCategory, fetchAddChecklistCategory,
  fetchUpdateChecklistCategory, addPublishChecklistCategory,
  removePublishChecklistCategory, fetchChecklistCategories, fetchChecklistCategoryReferences,
} from './checklistCategoryUiActions';

/**
 * A selector for those Checklist Category reference areas in the domain.
 * @param {state} state The redux state for the application.
 */
export const selectChecklistCategoryReferencesMap = state => state.via.domain.checklistCategoryReferences;

/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the Checklist Category being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.checklistCategories.editState;

/**
 * A selector for finding the current publishing checklist category codes.
 * @param {object} state The redux state of the application.
 * @returns An array of Checklist Category codes.
 * @example const loadingChecklistCategoryCodes = selectLoadingChecklistCategoryCodes(state);
 */
export const selectLoadingChecklistCategoryCodes = state => state.via.ui.categories.pendingPublishChecklistCategories;

/**
 * A selector that returns the state of the 'FETCH_CHECKLIST_CATEGORIES' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const checklistCategoryRequest = selectChecklistCategoriesRequest(state);
 */
export const selectChecklistCategoriesRequest = (state) => {
  const requestId = state.via.ui.checklistCategories.requestChecklistCategoriesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the current 'FETCH_CREATE_CHECKLIST_CATEGORY' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addChecklistCategoryRequest = selectAddChecklistCategoryRequest(state);
 */
export const selectSubmitChecklistCategoryRequest = (state) => {
  const requestId = state.via.ui.checklistCategories.editState.requestSubmitChecklistCategoriesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector that returns the state of the 'FETCH_CHECKLIST_CATEGORY_REFERENCES' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const checklistCategoryReferencesRequest = selectChecklistCategoryReferencesRequest(state);
 */
export const selectChecklistCategoryReferencesRequest = (state) => {
  const requestId = state.via.ui.categories.requestChecklistCategoryReferencesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for those Checklist Category reference areas loaded by the Checklist Category Configuration screen.
 * @param {state} state The redux state for the application.
 * @returns The Checklist Category references areas that were loaded.
 * @example const checklistCategoryReferences = selectChecklistCategoryReferences(state);
 */
export const selectChecklistCategoryReferences = (state) => {
  const checklistCategoryReferencesMap = selectChecklistCategoryReferencesMap(state);
  const checklistCategoryReferences = Object.values(checklistCategoryReferencesMap);

  return checklistCategoryReferences || [];
};

const intitialEditingState = {
  isEditing: false,
  categoryCode: null,
  requestSubmitChecklistCategoryId: null,
};

const checklistCategoriesInitialState = {
  editState: intitialEditingState, // Is a Checklist Category being edited/created.
  pendingPublishChecklistCategories: [], // Checklist Categories being submitted for publishing.
  requestChecklistCategoriesId: null, // The request id for loading Checklist Categories.
  requestChecklistCategoryReferencesId: null, // The request id for loading those items that are referenced by a Checklist Category.
};

/**
 * A reducer for handling the UI state changes for the Checklist Category Configuration screen.
 */
export const checklistCategoriesReducer = createReducer(checklistCategoriesInitialState, {
  // The user has started editing the Checklist Category.
  [editChecklistCategoryNew]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: true,
      categoryCode: action.payload,
    },
  }),

  // The user has stopped editing the Checklist Category.
  [cancelEditChecklistCategory]: state => ({
    ...state,
    editState: {
      ...intitialEditingState,
    },
  }),

  // A new requst has been made to add a Checklist Category.
  [fetchAddChecklistCategory]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitChecklistCategoryId: action.payload,
    },
  }),

  // A new request has been made to update a Checklist Category.
  [fetchUpdateChecklistCategory]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitChecklistCategoryId: action.payload,
    },
  }),

  // A Checklist Category is being published. So add it to state.
  [addPublishChecklistCategory]: (state, action) => ({
    ...state,
    pendingPublishChecklistCategories: [...state.pendingPublishChecklistCategories, action.payload],
  }),

  // A Checklist Category has finished publishing. Remove it from state.
  [removePublishChecklistCategory]: (state, action) => ({
    ...state,
    pendingPublishChecklistCategories: state.pendingPublishChecklistCategories.filter(code => `${code}` !== `${action.payload}`),
  }),

  // A new request has been made for the Checklist Categories.
  [fetchChecklistCategories]: (state, action) => ({
    ...state,
    requestChecklistCategoriesId: action.payload,
  }),

  // A new request has been made for those areas referenced by a Checklist Category.
  [fetchChecklistCategoryReferences]: (state, action) => ({
    ...state,
    requestChecklistCategoryReferencesId: action.payload,
  }),

});

export default checklistCategoriesReducer;
